import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  ICard,
  ICheckoutCart,
  IPaymentTokenAccount,
  PaymentMethod,
  PaymentMethodTypesEnum,
  PaymentMethodsType,
} from '@monofront/types';
import { getIsCardNotExpired } from '@monofront/business/utils';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import CharacterSad from '@monofront/design-system/visuals/Illustrations/CharacterSad';

import { usePaymentMethods } from '@features/PaymentMethods/PaymentMethodContext';
import logger from '@utils/logger';
import { useAuthentication } from '@context/businessContainer';
import CardDetails from '@components/CardDetails';
import PaypalRow from '@components/PaypalRow';
import { useCheckoutPayment } from '../contexts';

interface IPaymentMethodsListExistingProps {
  checkoutCart: ICheckoutCart;
}

interface IAvailablePaymentMethods {
  paymentMethod: PaymentMethod;
  paymentMethodType: PaymentMethodTypesEnum;
  paymentMethodCode: string;
}

/**
 * List customer's existing payment methods available to use on the checkout.
 * Ie. all customer's saved PM except SEPA and old Stripe Cards.
 * Also, pre-select the first usable PM.
 */
const PaymentMethodsListExisting: React.FC<
  IPaymentMethodsListExistingProps
> = ({ checkoutCart }) => {
  const tAccount = useTranslations('Account');
  const tCheckout = useTranslations('Checkout');
  const tCheckoutPayment = useTranslations('Checkout.Payment');
  const { authUser } = useAuthentication();
  const { error: paymentMethodsError } = usePaymentMethods();
  const {
    isOrderValidationInProgress,
    selectedPaymentRadioId,
    selectExistingPaymentMethod,
    savedPaymentMethods,
    newPaymentOptions,
  } = useCheckoutPayment();

  const availablePaymentMethods: IAvailablePaymentMethods[] = useMemo(() => {
    return savedPaymentMethods.reduce(
      (paymentsMethods, currentPaymentMethod) => {
        const associatedPaymentOption = newPaymentOptions?.find(
          (pm) => (pm.type as string) === currentPaymentMethod.source,
        );

        if (associatedPaymentOption) {
          // only return user's PM that can be associated to an OMS payment option.
          return [
            ...paymentsMethods,
            {
              paymentMethod: currentPaymentMethod,
              paymentMethodType: associatedPaymentOption.method,
              paymentMethodCode: associatedPaymentOption.code,
            },
          ];
        }

        const warnMessage = {
          message:
            '[checkout] Cannot display a user Payment Method with no associated OMS payment option.',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart?.id,
            pm_id: currentPaymentMethod.id,
          },
        };

        logger.warn(warnMessage.message, warnMessage.context);

        return paymentsMethods;
      },
      [] as IAvailablePaymentMethods[],
    );
  }, [savedPaymentMethods.length]);

  useEffect(() => {
    // Pre-select first existing PM when no PM has been chosen yet
    const usablePaymentMethods = availablePaymentMethods.filter(
      ({ paymentMethod }) => getIsCardNotExpired(paymentMethod),
    );

    if (usablePaymentMethods.length && !selectedPaymentRadioId) {
      selectExistingPaymentMethod(
        usablePaymentMethods[0].paymentMethod.id,
        {
          existingPaymentMethodId: usablePaymentMethods[0].paymentMethod.id,
          paymentMethodSource: usablePaymentMethods[0].paymentMethod.source,
          paymentMethodCode: usablePaymentMethods[0].paymentMethodCode,
        },
        usablePaymentMethods[0].paymentMethodType,
      );
    }
  }, [availablePaymentMethods.length]);

  if (paymentMethodsError) {
    return (
      <InformativeBox
        backgroundColor={'var(--clr-secondary-3)'}
        hasImageOnMobile
        icon={<CharacterSad width={34} height={34} aria-hidden="true" />}
        smallBox={true}
        UNSAFE_className={'tw-mb-5'}
      >
        <p className="tw-lf-body-regular-sm tw-leading-normal">
          {tCheckoutPayment.rich('messageCannotFetchSavedPM', {
            strong: (children) => <strong>{children}</strong>,
            br: () => <br />,
          })}
        </p>
      </InformativeBox>
    );
  }

  if (!availablePaymentMethods.length) return null;

  return (
    <section className="payment-methods-list-existing">
      <h2 className="tw-lf-body-xbold-md tw-mb-2">
        {tCheckout('useSavedPaymentMethod')}
      </h2>

      <ul>
        {availablePaymentMethods.map(
          ({ paymentMethod, paymentMethodCode, paymentMethodType }) => (
            <li key={paymentMethod.id}>
              <RadioLine
                id={paymentMethod.id}
                name="checkout-available-payment-list"
                value={paymentMethod.id}
                checked={selectedPaymentRadioId === paymentMethod.id}
                handleChecked={() => {
                  selectExistingPaymentMethod(
                    paymentMethod.id,
                    {
                      existingPaymentMethodId: paymentMethod.id,
                      paymentMethodCode: paymentMethodCode,
                      paymentMethodSource: paymentMethod.source,
                    },
                    paymentMethodType,
                  );
                }}
                required
                disabled={
                  isOrderValidationInProgress ||
                  (paymentMethod.paymentType === PaymentMethodsType.Card &&
                    !getIsCardNotExpired(paymentMethod))
                }
                isTextBoldWhenChecked
                isVerticallyCentered
                dataTestId={`CheckoutPayment-RadioLine-existing-payment-${paymentMethod.id}`}
              >
                {paymentMethod.paymentType === PaymentMethodsType.Card && (
                  <CardDetails
                    paymentMethod={paymentMethod as ICard}
                    tags={
                      !getIsCardNotExpired(paymentMethod)
                        ? [
                            <span
                              key="tag-expired"
                              className="tw-lf-tag tw-lf-tag-md tw-lf-tag-decorative-danger"
                            >
                              {tAccount('expired')}{' '}
                            </span>,
                          ]
                        : undefined
                    }
                  />
                )}
                {paymentMethod.paymentType ===
                  PaymentMethodsType.PaymentTokenAccount && (
                  <div className="paypal tw-flex tw-items-center">
                    <PaypalRow
                      paymentMethod={paymentMethod as IPaymentTokenAccount}
                    />
                  </div>
                )}
              </RadioLine>
            </li>
          ),
        )}
      </ul>
    </section>
  );
};

export default PaymentMethodsListExisting;
